import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { InsuranceActionListenerCallback, InsuranceActionListenerList } from '@typesApp/InsuranceActionListener'
import { ProductQuote, ProductQuoteResult } from '@typesApp/ProductQuote'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { AppState } from '@redux/store'

type ProductQuoteStatus = {
  isLoading?: boolean
  isError?: boolean
  isSuccess?: boolean
  productQuotes?: ProductQuote[]
}

export type InsuranceMemberState = {
  isProductQuoteError?: boolean
  isToggleClicked?: boolean
  insuranceActionListeners?: InsuranceActionListenerList
  productQuoteStatus?: ProductQuoteStatus
}

const initialState: InsuranceMemberState = {}

export const insuranceMemberSlice = createSlice({
  name: 'insuranceMember',
  initialState: initialState,
  reducers: {
    addInsuranceActionListener(
      state,
      action: PayloadAction<{ listener: InsuranceActionListenerCallback; isLegacy?: boolean }>
    ) {
      if (!state.insuranceActionListeners) {
        state.insuranceActionListeners = {
          listeners: [],
        }
      }
      state.insuranceActionListeners.isLegacy = action.payload.isLegacy
      state.insuranceActionListeners.listeners?.push(action.payload.listener)
    },
    removeInsuranceActionListener(state, action: PayloadAction<InsuranceActionListenerCallback>) {
      if (state.insuranceActionListeners) {
        const handlers = state.insuranceActionListeners.listeners ?? []
        const remainingHandlers = handlers.filter(handler => handler !== action.payload)
        state.insuranceActionListeners.listeners = remainingHandlers
      }
    },
    setProductQuoteLoading(state) {
      state.productQuoteStatus = {
        isLoading: true,
      }
    },
    setProductQuoteError(state, action: PayloadAction<boolean>) {
      state.productQuoteStatus = {
        ...state.productQuoteStatus,
        isError: action.payload,
        isLoading: false,
      }
    },
    setProductQuoteSuccess(state, action: PayloadAction<ProductQuoteResult>) {
      const productQuotes = Array.isArray(action.payload?.productQuotes) ? action.payload.productQuotes : []

      state.productQuoteStatus = {
        ...state.productQuoteStatus,
        isSuccess: productQuotes.length > 0,
        isLoading: false,
        productQuotes,
      }
    },
    setToggleClicked(state, action: PayloadAction<boolean>) {
      state.isToggleClicked = action.payload
    },
    resetInsuranceMemberState(state) {
      // always retain the listeners, only commerce F/E can remove them
      // always retain the product quote status for the toggle message
      return {
        ...initialState,
        productQuoteStatus: state.productQuoteStatus,
        insuranceActionListeners: state.insuranceActionListeners,
      }
    },
  },
  extraReducers: builder => {
    // reset product quote status on delete claim record or toggle click
    builder.addMatcher(
      isAnyOf(
        claimRecordsApi.endpoints.deleteClaimRecord.matchFulfilled,
        claimRecordsApi.endpoints.changeInsuranceStatus.matchFulfilled
      ),
      state => {
        state.productQuoteStatus = undefined
      }
    )
  },
})

export const {
  addInsuranceActionListener,
  removeInsuranceActionListener,
  setProductQuoteLoading,
  setProductQuoteError,
  setProductQuoteSuccess,
  setToggleClicked,
  resetInsuranceMemberState,
} = insuranceMemberSlice.actions

export const insuranceListenersSelector = (state: AppState): InsuranceActionListenerList | undefined =>
  state.insuranceMember.insuranceActionListeners
export const legacyInsuranceListenerSelector = (state: AppState): boolean | undefined =>
  state.insuranceMember.insuranceActionListeners?.isLegacy
export const productQuoteStatusSelector = (state: AppState): ProductQuoteStatus | undefined =>
  state.insuranceMember.productQuoteStatus
export const isToggleClickedSelector = (state: AppState): boolean | undefined => state.insuranceMember.isToggleClicked
